// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import { isRtlLang, pickLangValue } from "@/utils";
import type { TyEditorDiscount, TyEditorPrice } from "@/types";
import CategoryPickerPopup, { type CategoryPickerOption } from "@/UI/popups/CategoryPickerPopup";
import DiscountPopup from "@/UI/popups/DiscountPopup";
import PricesPopup from "@/UI/popups/PricesPopup";
import Cell from "./Cell";
import { isProductRowBlank, isRowDirty, type TyProductDraft, type TyRow } from "./model";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    rows: TyRow<TyProductDraft>[];
    invalidKeys: Set<string>;
    categoryOptions: CategoryPickerOption[];
    onChange: (key: string, patch: Partial<TyProductDraft>) => void;
    onRevert: (key: string) => void;
    onRemove: (key: string) => void;
    /** Enter on the last row appends one and lands in the same column. */
    onAddRow: (column: string) => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ProductsGrid({ rows, invalidKeys, categoryOptions, onChange, onRevert, onRemove, onAddRow }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { languages } = useStoreLanguages();
    const { addComp, removeComp } = usePopup();

    const rowName = (row: TyRow<TyProductDraft>) => pickLangValue(row.draft.name, lang);

    const openPopup = (name: string, Component: Parameters<typeof addComp>[0]["Component"], details: unknown) => {
        removeComp(name);
        addComp({ name, Component, place: "center", anim: "scale", cover: "shaded", details });
    };

    const openCategoryPicker = (row: TyRow<TyProductDraft>) =>
        openPopup("category-picker", CategoryPickerPopup, {
            options: categoryOptions,
            selected: row.draft.category,
            onConfirm: (selected: string[]) => onChange(row.key, { category: selected }),
        });

    const openPrices = (row: TyRow<TyProductDraft>) =>
        openPopup("prices-editor", PricesPopup, {
            productName: rowName(row),
            prices: row.draft.prices,
            onConfirm: (prices: TyEditorPrice[]) => onChange(row.key, { prices }),
        });

    const openDiscount = (row: TyRow<TyProductDraft>) =>
        openPopup("discount-editor", DiscountPopup, {
            productName: rowName(row),
            discount: row.draft.discount,
            onConfirm: (discount: TyEditorDiscount) => onChange(row.key, { discount }),
        });

    /** A row still holding nothing is abandoned on Escape rather than reverted to its empty self. */
    const escapeHandler = (row: TyRow<TyProductDraft>) => (isProductRowBlank(row) ? () => onRemove(row.key) : undefined);

    const priceLabel = (price: TyEditorPrice) => price.label?.find((entry) => entry.lang === lang)?.value ?? price.label?.[0]?.value ?? "";

    return (
        <table className="editor-table">
            <thead>
                <tr>
                    <th className="editor-th editor-th--index">#</th>
                    {languages.map((entry) => (
                        <th key={entry} className="editor-th editor-th--name">
                            {`${t.menuEditor.nameColumn} · ${t.languages[entry]}`}
                        </th>
                    ))}
                    <th className="editor-th editor-th--prices">{t.menuEditor.prices}</th>
                    <th className="editor-th editor-th--cats">{t.menuEditor.categories}</th>
                    <th className="editor-th editor-th--discount">{t.menuEditor.discount}</th>
                    {languages.map((entry) => (
                        <th key={entry} className="editor-th editor-th--desc">
                            {`${t.menuEditor.descColumn} · ${t.languages[entry]}`}
                        </th>
                    ))}
                    <th className="editor-th editor-th--flag">{t.menuEditor.active}</th>
                    <th className="editor-th editor-th--flag">{t.menuEditor.hidden}</th>
                    <th className="editor-th editor-th--tools" aria-label={t.menuEditor.rowTools} />
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => {
                    const dirty = isRowDirty(row);
                    const invalid = invalidKeys.has(row.key);
                    const discount = row.draft.discount;

                    return (
                        <tr
                            key={row.key}
                            data-row-key={row.key}
                            className={`editor-tr${dirty ? " editor-tr--dirty" : ""}${invalid ? " editor-tr--invalid" : ""}`}
                        >
                            <td className="editor-td editor-td--index">
                                <span className="editor-index">{index + 1}</span>
                                {!row._id && <span className="editor-new-badge">{t.menuEditor.newBadge}</span>}
                                {/* Pictures are managed in the dashboard — shown here only so a row is recognisable. */}
                                {row.draft.picture && <img className="editor-thumb" src={row.draft.picture} alt="" loading="lazy" />}
                            </td>

                            {languages.map((entry) => (
                                <td key={entry} className="editor-td">
                                    <Cell
                                        value={row.draft.name[entry] ?? ""}
                                        onChange={(value) => onChange(row.key, { name: { ...row.draft.name, [entry]: value } })}
                                        onRevert={() => onRevert(row.key)}
                                        onEscapeEmpty={escapeHandler(row)}
                                        onEnterPastEnd={() => onAddRow(`name_${entry}`)}
                                        column={`name_${entry}`}
                                        rowIndex={index}
                                        dirty={dirty && row.draft.name[entry] !== row.saved?.name[entry]}
                                        dir={isRtlLang(entry) ? "rtl" : "ltr"}
                                        placeholder={t.languages[entry]}
                                    />
                                </td>
                            ))}

                            {/* Every tier is listed — a menu row that hides half its prices is not reviewable. */}
                            <td className="editor-td">
                                <button
                                    type="button"
                                    className={`editor-chips${row.draft.prices.length === 0 ? " editor-chips--empty" : ""}`}
                                    onClick={() => openPrices(row)}
                                >
                                    {row.draft.prices.length === 0 ? (
                                        <span className="editor-chips-placeholder">{t.menuEditor.addPrice}</span>
                                    ) : (
                                        row.draft.prices.map((price, priceIndex) => (
                                            <span className="editor-chip editor-chip--price" key={price._id ?? priceIndex}>
                                                <span className="editor-chip-amount">{price.amount}</span>
                                                {priceLabel(price) && <span className="editor-chip-note">{priceLabel(price)}</span>}
                                            </span>
                                        ))
                                    )}
                                </button>
                            </td>

                            <td className="editor-td">
                                <button
                                    type="button"
                                    className={`editor-chips${row.draft.category.length === 0 ? " editor-chips--empty" : ""}`}
                                    onClick={() => openCategoryPicker(row)}
                                >
                                    {row.draft.category.length === 0 ? (
                                        <span className="editor-chips-placeholder">{t.menuEditor.pickCategories}</span>
                                    ) : (
                                        row.draft.category.map((id) => (
                                            <span className="editor-chip" key={id}>
                                                {categoryOptions.find((option) => option._id === id)?.label ?? "—"}
                                            </span>
                                        ))
                                    )}
                                </button>
                            </td>

                            <td className="editor-td">
                                <button
                                    type="button"
                                    className={`editor-chips${discount?.active ? "" : " editor-chips--empty"}`}
                                    onClick={() => openDiscount(row)}
                                >
                                    {discount?.active ? (
                                        <span className="editor-chip editor-chip--discount">
                                            {discount.amount}
                                            {discount.type === "percentage" ? "%" : ""}
                                        </span>
                                    ) : (
                                        <span className="editor-chips-placeholder">{t.menuEditor.noDiscount}</span>
                                    )}
                                </button>
                            </td>

                            {languages.map((entry) => (
                                <td key={entry} className="editor-td">
                                    <Cell
                                        value={row.draft.desc[entry] ?? ""}
                                        onChange={(value) => onChange(row.key, { desc: { ...row.draft.desc, [entry]: value } })}
                                        onRevert={() => onRevert(row.key)}
                                        onEscapeEmpty={escapeHandler(row)}
                                        onEnterPastEnd={() => onAddRow(`desc_${entry}`)}
                                        column={`desc_${entry}`}
                                        rowIndex={index}
                                        dirty={dirty && row.draft.desc[entry] !== row.saved?.desc[entry]}
                                        dir={isRtlLang(entry) ? "rtl" : "ltr"}
                                    />
                                </td>
                            ))}

                            <td className="editor-td editor-td--flag">
                                <button
                                    type="button"
                                    className={`editor-flag${row.draft.active ? " editor-flag--on" : ""}`}
                                    onClick={() => onChange(row.key, { active: !row.draft.active })}
                                    aria-pressed={row.draft.active}
                                    aria-label={t.menuEditor.active}
                                />
                            </td>

                            <td className="editor-td editor-td--flag">
                                <button
                                    type="button"
                                    className={`editor-flag${row.draft.hidden ? " editor-flag--on" : ""}`}
                                    onClick={() => onChange(row.key, { hidden: !row.draft.hidden })}
                                    aria-pressed={row.draft.hidden}
                                    aria-label={t.menuEditor.hidden}
                                />
                            </td>

                            <td className="editor-td editor-td--tools">
                                {dirty && (
                                    <button
                                        type="button"
                                        className="editor-row-btn"
                                        onClick={() => (row._id ? onRevert(row.key) : onRemove(row.key))}
                                        aria-label={row._id ? t.menuEditor.revertRow : t.menuEditor.removeRow}
                                        title={row._id ? t.menuEditor.revertRow : t.menuEditor.removeRow}
                                    >
                                        <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
